// EditorMenuControls.tsx

import { useTheme } from "@mui/material";
import {
  MenuButtonAddTable,
  MenuButtonBlockquote,
  MenuButtonBold,
  MenuButtonBulletedList,
  MenuButtonCode,
  MenuButtonCodeBlock,
  MenuButtonEditLink,
  MenuButtonHighlightColor,
  MenuButtonHorizontalRule,
  MenuButtonImageUpload,
  MenuButtonIndent,
  MenuButtonItalic,
  MenuButtonOrderedList,
  MenuButtonRedo,
  MenuButtonRemoveFormatting,
  MenuButtonStrikethrough,
  MenuButtonSubscript,
  MenuButtonSuperscript,
  MenuButtonTaskList,
  MenuButtonTextColor,
  MenuButtonUnderline,
  MenuButtonUndo,
  MenuButtonUnindent,
  MenuControlsContainer,
  MenuDivider,
  MenuSelectFontFamily,
  MenuSelectFontSize,
  MenuSelectHeading,
  MenuSelectTextAlign,
  isTouchDevice,
} from "mui-tiptap";
import { CustomInlineStyleExtension, CustomInlineStyleControl } from './CustomInlineStyle';
import { ParagraphStyleExtension, ParagraphStyleControl } from './ParagraphStyleExtension';
import CssEditorPopup from './CssEditorPopup';
import { Button, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';

import { useState, useEffect } from "react";
import InsertContentPopup from "./InsertContentPopup";
import type { Editor } from '@tiptap/react';
import { fetchClassNames } from './cssParser';

// Define the ExtensionProps type
export interface ExtensionProps {
  state: string;
  query: string;
  htmlStyle: string;
  imageHeight?: string;
  numImages?: string;
  linkText?: string;
  linkAlbumId?: string;
  imageUrl?: string;
}

interface EditorMenuControlsProps {
  editor?: Editor | null;
  onInsert: (extension: string, props: ExtensionProps) => void;
  onOpenInsertPopup: () => void;
}

const EditorMenuControls: React.FC<EditorMenuControlsProps> = ({ editor, onInsert, onOpenInsertPopup }) => {
  const [showInsertPopup, setShowInsertPopup] = useState(false);
  const theme = useTheme();
  const [paragraphStyles, setParagraphStyles] = useState<string[]>([]);
  const [spanStyles, setSpanStyles] = useState<string[]>([]);
  const [cssEditorOpen, setCssEditorOpen] = useState<boolean>(false);
  const [cssFileToEdit, setCssFileToEdit] = useState<string>('');

  useEffect(() => {
    const loadParagraphStyles = async () => {
      const styles = await fetchClassNames('paragraphStyles.css');
      setParagraphStyles(styles);
    };
    loadParagraphStyles();
  }, []);

  useEffect(() => {
    const loadSpanStyles = async () => {
      const styles = await fetchClassNames('inlineStyles.css');
      setSpanStyles(styles);
    };
    loadSpanStyles();
  }, []);

  const handleCancelInsert = () => {
    setShowInsertPopup(false);
  };

  const openCssEditor = (filename: string) => {
    setCssFileToEdit(filename);
    setCssEditorOpen(true);
  };

  const closeCssEditor = () => {
    setCssEditorOpen(false);
    setCssFileToEdit('');
    // Optionally, re-fetch styles after editing
    if (cssFileToEdit === 'paragraphStyles.css') {
      fetchClassNames('paragraphStyles.css').then(setParagraphStyles);
    } else if (cssFileToEdit === 'inlineStyles.css') {
      fetchClassNames('inlineStyles.css').then(setSpanStyles);
    }
  };

  // Transform spanStyles to the required format
  const spanStyleOptions = spanStyles.map((style) => ({
    value: style,
    label: style
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' '),
  }));

  const paragraphStyleOptions = paragraphStyles.map((style) => ({
    value: style,
    label: style
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' '),
  }));

  return (
    <MenuControlsContainer>
      <button onClick={onOpenInsertPopup}>Insert Content</button>

      <MenuDivider />

      <MenuDivider />

      <MenuSelectHeading />

      <MenuSelectFontFamily
        options={[
          { label: "Comic Sans", value: "Comic Sans MS, Comic Sans" },
          { label: "Cursive", value: "cursive" },
          { label: "Monospace", value: "monospace" },
          { label: "Serif", value: "serif" },
        ]}
      />


      <MenuDivider />

      <MenuSelectFontSize />

      <div style={{ display: 'flex', alignItems: 'center' }}>
        <CustomInlineStyleControl editor={editor} styles={spanStyleOptions} />
        <Tooltip title="Edit Inline Styles">
          <Button
            size="small"
            onClick={() => openCssEditor('inlineStyles.css')}
            startIcon={<EditIcon />}
          >
            Edit CSS
          </Button>
        </Tooltip>
      </div>
      <MenuDivider />
      
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <ParagraphStyleControl editor={editor} styles={paragraphStyleOptions} />
        <Tooltip title="Edit Paragraph Styles">
          <Button
            size="small"
            onClick={() => openCssEditor('paragraphStyles.css')}
            startIcon={<EditIcon />}
          >
            Edit CSS
          </Button>
        </Tooltip>
      </div>

      <MenuDivider />

      <MenuButtonBold />
      <MenuButtonItalic />
      <MenuButtonUnderline />
      <MenuButtonStrikethrough />
      <MenuButtonSubscript />
      <MenuButtonSuperscript />

      <MenuDivider />

      <MenuButtonTextColor
        defaultTextColor={theme.palette.text.primary}
        swatchColors={[
          { value: "#000000", label: "Black" },
          { value: "#ffffff", label: "White" },
          { value: "#888888", label: "Grey" },
          { value: "#ff0000", label: "Red" },
          { value: "#ff9900", label: "Orange" },
          { value: "#ffff00", label: "Yellow" },
          { value: "#00d000", label: "Green" },
          { value: "#0000ff", label: "Blue" },
        ]}
      />

      <MenuButtonHighlightColor
        swatchColors={[
          { value: "#595959", label: "Dark grey" },
          { value: "#dddddd", label: "Light grey" },
          { value: "#ffa6a6", label: "Light red" },
          { value: "#ffd699", label: "Light orange" },
          { value: "#ffff00", label: "Yellow" },
          { value: "#99cc99", label: "Light green" },
          { value: "#90c6ff", label: "Light blue" },
          { value: "#8085e9", label: "Light purple" },
        ]}
      />

      <MenuDivider />

      <MenuButtonEditLink />

      <MenuDivider />

      <MenuSelectTextAlign />

      <MenuDivider />

      <MenuButtonOrderedList />
      <MenuButtonBulletedList />
      <MenuButtonTaskList />

      {/* On touch devices, we'll show indent/unindent buttons */}
      {isTouchDevice() && (
        <>
          <MenuButtonIndent />
          <MenuButtonUnindent />
        </>
      )}

      <MenuDivider />

      <MenuButtonBlockquote />

      <MenuDivider />

      <MenuButtonCode />
      <MenuButtonCodeBlock />

      <MenuDivider />

      <MenuButtonImageUpload
        onUploadFiles={async (files) => {
          const uploadedImages = await Promise.all(
            files.map(async (file) => {
              // Create a FormData object
              const formData = new FormData();
              formData.append('file', file);
              // Include any additional data if required
              formData.append('action', 'uploadImage'); // Custom action if needed

              // Send the POST request to your server
              const response = await fetch(`${process.env.REACT_APP_USERMANAGEMENT_URL}`, {
                method: 'POST',
                body: formData,
              });

              // Check if the response is OK
              if (!response.ok) {
                throw new Error('File upload failed');
              }

              // Parse the JSON response
              const data = await response.json();

              // Return the image object with the URL provided by the server
              return {
                src: data.fileUrl, // Ensure your server returns 'fileUrl'
                alt: file.name,
              };
            })
          );

          return uploadedImages;
        }}
      />

      <MenuDivider />

      <MenuButtonHorizontalRule />
      <MenuButtonAddTable />

      <MenuDivider />

      <MenuButtonRemoveFormatting />

      <MenuDivider />

      <MenuButtonUndo />
      <MenuButtonRedo />

      {/* Custom Paragraph Styles Dropdown */}
      <MenuDivider />

      <CssEditorPopup
        open={cssEditorOpen}
        onClose={closeCssEditor}
        filename={cssFileToEdit}
      />

    </MenuControlsContainer>
  );
};

export default EditorMenuControls;
